import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SUBJECTS, LEVELS, PLANS, subjectStyle } from "@/lib/constants";
import { User, Bell, Crown, Save, Moon, Sun, Copy, RefreshCw, Users, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({
    name: user?.name || "",
    level: user?.level || "Seconde",
    subjects: user?.subjects || [],
    notifications: user?.notifications ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [dark, setDark] = useState(localStorage.getItem("studykit_theme") === "dark");
  const [parentCode, setParentCode] = useState("");
  const [linkedChildren, setLinkedChildren] = useState([]);
  const isParent = user?.role === "parent";

  useEffect(() => {
    if (isParent) {
      api.get("/parent/children").then(({ data }) => setLinkedChildren(data)).catch(() => {});
    } else {
      api.get("/student/parent-code").then(({ data }) => setParentCode(data.code)).catch(() => {});
    }
  }, [isParent]);

  const copyCode = () => {
    navigator.clipboard.writeText(parentCode).then(() => toast.success("Code copié !"));
  };

  const regenerateCode = async () => {
    try {
      const { data } = await api.post("/student/parent-code/regenerate");
      setParentCode(data.code);
      toast.success("Nouveau code généré — l'ancien ne fonctionne plus pour les nouvelles liaisons.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const unlinkChild = async (child) => {
    try {
      await api.delete(`/parent/unlink/${child.id}`);
      toast.success(`${child.name} délié`);
      setLinkedChildren(linkedChildren.filter((c) => c.id !== child.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleDark = () => {
    const next = !dark;
    setDark(next);
    document.documentElement.classList.toggle("dark", next);
    localStorage.setItem("studykit_theme", next ? "dark" : "light");
    toast.success(next ? "Mode sombre activé 🌙" : "Mode clair activé ☀️");
  };

  const toggleSubject = (s) => {
    setForm({
      ...form,
      subjects: form.subjects.includes(s) ? form.subjects.filter((x) => x !== s) : [...form.subjects, s],
    });
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/profile", form);
      setUser(data);
      toast.success("Profil mis à jour !");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const plan = PLANS[user?.plan || "gratuit"];

  return (
    <div className="space-y-6 max-w-3xl" data-testid="profile-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">Profil & paramètres</h1>
        <p className="text-slate-500 text-sm sm:text-base mt-1">Personnalise ProEdu selon ta classe et tes matières.</p>
      </div>

      {/* Account summary */}
      <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm flex flex-wrap items-center gap-5" data-testid="profile-summary">
        <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-violet-500 flex items-center justify-center text-white text-2xl font-bold font-display">
          {user?.name?.[0]?.toUpperCase() || "?"}
        </div>
        <div className="flex-1 min-w-[200px]">
          <p className="text-lg font-semibold text-slate-900" data-testid="profile-name-display">{user?.name}</p>
          <p className="text-sm text-slate-500" data-testid="profile-email-display">{user?.email}</p>
          <div className="flex gap-2 mt-2 flex-wrap">
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700" data-testid="profile-level-badge">{user?.level}</span>
            <span className="text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full bg-slate-100 text-slate-700" data-testid="profile-plan-badge">{plan.name}</span>
          </div>
        </div>
        <Link to="/pricing" data-testid="profile-manage-plan" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-indigo-100 hover:text-indigo-700 transition-colors duration-200">
          <Crown className="h-4 w-4" /> Gérer ma formule
        </Link>
      </div>

      <form onSubmit={save} className="space-y-6">
        {/* Infos */}
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="profile-info-section">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-5">
            <User className="h-5 w-5 text-indigo-600" /> Informations du compte
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Prénom</label>
              <input type="text" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="profile-name-input" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1.5 block">Niveau scolaire</label>
              <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} data-testid="profile-level-select" className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm bg-white">
                {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Subjects */}
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="profile-subjects-section">
          <h2 className="text-lg font-semibold text-slate-900 mb-1">Mes matières</h2>
          <p className="text-xs text-slate-500 mb-5">Utilisées pour personnaliser ton planning de révision IA (Premium).</p>
          <div className="flex flex-wrap gap-2">
            {SUBJECTS.map((s) => {
              const active = form.subjects.includes(s);
              return (
                <button
                  type="button"
                  key={s}
                  onClick={() => toggleSubject(s)}
                  data-testid={`profile-subject-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                  className={`px-4 py-2 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                    active ? subjectStyle(s) + " ring-2 ring-indigo-400/40" : "bg-white text-slate-500 border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {s}
                </button>
              );
            })}
          </div>
        </div>

        {/* Preferences */}
        <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="profile-prefs-section">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-5">
            <Bell className="h-5 w-5 text-amber-500" /> Préférences
          </h2>
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <p className="text-sm font-medium text-slate-800">Rappels de devoirs importants</p>
              <p className="text-xs text-slate-500 mt-0.5">Affiche une alerte pour les devoirs urgents et importants.</p>
            </div>
            <button
              type="button"
              onClick={() => setForm({ ...form, notifications: !form.notifications })}
              data-testid="profile-notifications-toggle"
              className={`relative h-7 w-12 rounded-full transition-colors duration-200 ${form.notifications ? "bg-indigo-600" : "bg-slate-200"}`}
            >
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${form.notifications ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </label>
          <label className="flex items-center justify-between cursor-pointer mt-5 pt-5 border-t border-slate-100">
            <div>
              <p className="text-sm font-medium text-slate-800 flex items-center gap-2">
                {dark ? <Moon className="h-4 w-4 text-indigo-500" /> : <Sun className="h-4 w-4 text-amber-500" />}
                Mode sombre
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Un thème sombre pour réviser le soir sans se fatiguer les yeux.</p>
            </div>
            <button
              type="button"
              onClick={toggleDark}
              data-testid="profile-darkmode-toggle"
              className={`relative h-7 w-12 rounded-full transition-colors duration-200 ${dark ? "bg-indigo-600" : "bg-slate-200"}`}
            >
              <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform duration-200 ${dark ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </label>
        </div>

        {/* Suivi parent */}
        {isParent ? (
          <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="profile-children-section">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-indigo-600" /> Enfants liés
            </h2>
            <p className="text-xs text-slate-500 mb-5">Les comptes élèves que tu suis actuellement.</p>
            {linkedChildren.length === 0 ? (
              <p className="text-sm text-slate-400">Aucun enfant lié. Ajoute-le depuis la page « Mes enfants » avec son code.</p>
            ) : (
              <ul className="space-y-2">
                {linkedChildren.map((c) => (
                  <li key={c.id} data-testid={`linked-child-${c.id}`} className="flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 border border-slate-100">
                    <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center text-sm font-bold">{c.name[0]?.toUpperCase()}</div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-800">{c.name}</p>
                      <p className="text-xs text-slate-500">{c.level}</p>
                    </div>
                    <button onClick={() => unlinkChild(c)} data-testid={`unlink-child-${c.id}`} className="p-2 rounded-lg text-slate-300 hover:text-rose-500 hover:bg-rose-50">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-slate-200/80 p-6 shadow-sm" data-testid="profile-parent-code-section">
            <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2 mb-1">
              <Users className="h-5 w-5 text-indigo-600" /> Suivi parent
            </h2>
            <p className="text-xs text-slate-500 mb-5">
              Partage ce code avec tes parents pour qu'ils suivent ta progression (lecture seule — ils ne peuvent rien modifier).
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <span className="font-mono text-2xl font-bold tracking-[0.35em] px-6 py-3 rounded-2xl bg-indigo-50 text-indigo-700 border border-indigo-200" data-testid="parent-code-display">
                {parentCode || "……"}
              </span>
              <button onClick={copyCode} data-testid="copy-parent-code" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors duration-200">
                <Copy className="h-4 w-4" /> Copier
              </button>
              <button onClick={regenerateCode} data-testid="regenerate-parent-code" className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-100 text-slate-600 text-sm font-semibold hover:bg-slate-200 transition-colors duration-200">
                <RefreshCw className="h-4 w-4" /> Régénérer
              </button>
            </div>
          </div>
        )}

        <button type="submit" disabled={saving} data-testid="profile-save-button" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-indigo-600 text-white font-semibold text-sm hover:bg-indigo-700 transition-colors duration-200 disabled:opacity-60">
          <Save className="h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer les modifications"}
        </button>
      </form>
    </div>
  );
}
